import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Table } from "shared/app/Table";
import moment from "moment";
import { Dispatch, FC, SetStateAction, useMemo } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Label, SelectPayment } from "./SelectPayment";
import { useNavigate } from "react-router-dom";
import { AccountStatusBadge } from "./AccountStatusBadge";
import {
  AccountStatusVariant,
  PaymentStatuses,
  getEmptyState
} from "../constants";
import { Renter, SelectValue, Verification } from "../types";
import { useAuthContext } from "providers/Auth";
import { RvStatusBadge } from "./RvStatusBadge";

type Props = {
  rentersData: Renter[];
  hasSearchParams?: boolean;
  selectDefaultValue: PaymentStatuses;
  selectValues: SelectValue[];
  setSelectValues: Dispatch<SetStateAction<SelectValue[]>>;
  isLoading: boolean;
  sorted: { key: string; desc: boolean };
  setSorted: (sorted: { key: string; desc: boolean }) => void;
  activeTab: AccountStatusVariant;
  isReportingSingleRenter: boolean;
  reportSingleRenter: (data: {
    subscriberId: number;
    verificationId: number;
    verifications: Verification;
  }) => void;
};

export const SubscribersTable: FC<Props> = ({
  hasSearchParams,
  rentersData,
  selectDefaultValue,
  selectValues,
  setSelectValues,
  isLoading,
  sorted,
  setSorted,
  activeTab,
  reportSingleRenter,
  isReportingSingleRenter
}) => {
  const navigate = useNavigate();
  const { type } = useAuthContext();

  const handleChange = (id: number, newValue: string | number) => {
    setSelectValues((prev: SelectValue[]) => {
      const existingItemIndex = prev.findIndex((item) => item.id === id);
      if (newValue === selectDefaultValue)
        return prev.filter((item) => item.id !== id);
      else if (existingItemIndex !== -1)
        return prev.map((item, index) =>
          index === existingItemIndex ? { ...item, action: newValue } : item
        );
      else return [...prev, { id, action: newValue }];
    });
  };

  const handleReportClick = (renter: any) => {
    const selectedRenter = selectValues.find(
      (r) => r.id === renter.verification_id
    );
    const verifications = {
      action: !selectedRenter
        ? selectDefaultValue
        : selectedRenter.action === PaymentStatuses.PAID_ON_TIME
          ? PaymentStatuses.PAID_ON_TIME
          : selectedRenter.action === PaymentStatuses.NO_DATA
            ? PaymentStatuses.NO_DATA
            : PaymentStatuses.DELINQUENCY,
      months_delinquency:
        selectedRenter?.action === PaymentStatuses.PAID_ON_TIME ||
        selectedRenter?.action === PaymentStatuses.NO_DATA
          ? undefined
          : selectedRenter?.action
    };
    reportSingleRenter({
      subscriberId: renter.id,
      verificationId: renter.verification_id,
      verifications
    });
    setSelectValues([]);
  };

  const columns = useMemo(() => {
    const cols: ColumnDef<Renter, any>[] = [
      {
        id: "id",
        header: "Renter",
        accessorKey: "name",
        size: 220,
        cell: (p) => {
          return (
            <span className="text-sm text-content-1000 font-medium">
              {p.getValue()}
            </span>
          );
        }
      },
      {
        id: "status fixed_width",
        header: "Account status",
        accessorKey: "type",
        size: 140,
        cell: (p) => {
          return <AccountStatusBadge status={p.getValue()} />;
        }
      }
    ];
    if (type.isSDK)
      cols.push(
        {
          header: "Email",
          accessorKey: "email",
          size: 200,
          cell: (p) => {
            return (
              <span className="text-sm text-content-500">
                {p.getValue() ? p.getValue() : "--"}
              </span>
            );
          }
        },
        {
          header: "Subscription start",
          accessorKey: "subscription_start",
          size: 200,
          cell: (p) => {
            return (
              <span className="text-sm text-content-1000">
                {p.getValue()
                  ? moment(p.getValue()).format("MMM DD, YYYY")
                  : "--"}
              </span>
            );
          }
        },
        {
          header: "Subscription end",
          accessorKey: "subscription_end",
          size: 200,
          cell: (p) => {
            return (
              <span className="text-sm text-content-1000">
                {p.getValue()
                  ? moment(p.getValue()).format("MMM DD, YYYY")
                  : "--"}
              </span>
            );
          }
        }
      );
    else
      cols.push(
        {
          header: "Property",
          accessorKey: "property",
          cell: (p) => {
            const subscriber = p.row.original;
            return (
              <>
                <p className="text-sm text-content-1000 overflow-hidden text-ellipsis whitespace-nowrap">
                  {p.getValue()}
                </p>
                <p className="text-xs text-content-400 overflow-hidden text-ellipsis whitespace-nowrap">
                  {subscriber.address}
                </p>
              </>
            );
          }
        },
        {
          id: "fixed_width rent_amount",
          header: "Rent amount",
          accessorKey: "current_month_payment",
          size: 110,
          cell: (p) => (
            <p className="text-content-1000 text-sm">
              {p.getValue() ? `$${p.getValue().cents / 100}` : "--"}
            </p>
          )
        },
        {
          id: "fixed_width status",
          header: `${moment().subtract(1, "months").format("MMMM")} status`,
          accessorKey: "type",
          size: 214,
          cell: (p) => {
            const id = p.row.original.verification_id;
            const delinquencyOptions =
              p.row.original.available_delinquency_options;
            const options = delinquencyOptions?.length
              ? delinquencyOptions.map((option) => {
                  return {
                    value: option.months_delinquency,
                    label: (
                      <Label
                        title={option.name}
                        icon={
                          <div className="bg-danger-50 h-5 w-5 flex items-center justify-center">
                            <p className="text-danger-500 text-sm font-bold">
                              {option.months_delinquency}
                            </p>
                          </div>
                        }
                      />
                    )
                  };
                })
              : [];

            return (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                className="flex items-center justify-center gap-5"
              >
                {p.getValue() === AccountStatusVariant.NeedToReport &&
                !type.isSDK ? (
                  <SelectPayment
                    id={String(id)}
                    value={
                      selectValues.find((item) => item.id === id)?.action ||
                      selectDefaultValue
                    }
                    handleChange={(e) => handleChange(id, e.target.value)}
                    options={options}
                  />
                ) : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                p.row.original.rv_status ? (
                  <RvStatusBadge status={p.row.original.rv_status} />
                ) : (
                  "--"
                )}
              </div>
            );
          }
        },
        {
          id: "report_action fixed_width",
          header: "",
          size: 90,
          cell: (p) => {
            return p.row.original.type === AccountStatusVariant.NeedToReport ? (
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.XS}
                loading={isReportingSingleRenter}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  !isReportingSingleRenter && handleReportClick(p.row.original);
                }}
              >
                Report
              </Button>
            ) : (
              <div />
            );
          }
        }
      );

    return cols;
  }, [selectDefaultValue, selectValues, isReportingSingleRenter]);

  const table = useReactTable({
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    data: rentersData || [],
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <Table
      table={table}
      isLoading={isLoading}
      tdClassName="!py-2"
      onRowClick={(id) => navigate(`/rent-reporting/${id}`)}
      emptyStateTitle={
        hasSearchParams
          ? "No results found"
          : getEmptyState(activeTab, type.isSDK).title
      }
      emptyStateText={getEmptyState(activeTab, type.isSDK).subtitle}
      EmptyStateIcon={getEmptyState(activeTab, type.isSDK).icon}
      sortableKeys={["id"]}
      sorted={sorted}
      setSorted={setSorted}
    />
  );
};
